import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function DenialsTab() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [rationale, setRationale] = useState('');
  const [provNote, setProvNote] = useState('');
  // Workbench state (6A.4)
  const [workbench, setWorkbench] = useState(null);
  const [wbState, setWbState] = useState('');
  const [wbNextStep, setWbNextStep] = useState('');
  const [wbDueDate, setWbDueDate] = useState('');
  const [wbNote, setWbNote] = useState('');
  const [wbSaving, setWbSaving] = useState(false);
  // Execution state (6A.5)
  const [execTasks, setExecTasks] = useState([]);
  const [execCreating, setExecCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/rcm/denials/queue'); setQueue(r.data.queue || []); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (denId) => {
    setSelected(denId); setRationale(''); setProvNote('');
    setSuggestion(null); setWorkbench(null); setWbNote('');
    setExecTasks([]);
    try {
      const [dR, sR, wbR] = await Promise.all([
        api.get(`/rcm/denials/${denId}`),
        api.get(`/rcm/predictive/suggest/denial/${denId}`).catch(() => ({ data: null })),
        api.get(`/rcm/denial-workbench/${denId}`).catch(() => ({ data: null })),
      ]);
      setDetail(dR.data);
      setSuggestion(sR.data);
      if (wbR.data) {
        setWorkbench(wbR.data);
        setWbState(wbR.data.denial?.denial_state || 'open');
        setWbNextStep(wbR.data.denial?.next_step || '');
        setWbDueDate(wbR.data.denial?.due_date || '');
      }
      // Load execution tasks for this denial
      try {
        const eR = await api.get(`/rcm/execution?source_type=denial&source_id=${denId}`);
        setExecTasks(eR.data?.tasks || []);
      } catch {}
      const code = dR.data?.denial_reason_code;
      if (code) {
        try {
          const pR = await api.get(`/rcm/denial-playbooks/${code}`);
          setDetail(prev => ({ ...prev, _playbook: pR.data?.playbook, _playbook_match: pR.data?.match_type }));
        } catch {}
      }
    } catch { setDetail(null); }
  };

  const doAction = async (action, extra = {}) => {
    if (!selected) return;
    try {
      await api.post(`/rcm/denials/${selected}/set-action`, { action, action_rationale: rationale, provider_note: provNote, ...extra });
      setRationale(''); setProvNote('');
      load(); openDetail(selected);
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const saveWorkbenchProgress = async () => {
    if (!selected) return;
    setWbSaving(true);
    try {
      const result = await api.post(`/rcm/denial-workbench/${selected}/save`, {
        denial_state: wbState,
        next_step: wbNextStep,
        due_date: wbDueDate,
        work_note: wbNote,
      });
      setWorkbench(result.data);
      setWbNote(''); // Clear note input after save (notes are appended)
    } catch(e) { alert(e.response?.data?.detail || 'Save failed'); }
    finally { setWbSaving(false); }
  };

  // Execution bridge actions (6A.5)
  const createExecTask = async (actionType) => {
    if (!selected) return;
    setExecCreating(true);
    try {
      await api.post(`/rcm/execution/create-from-denial/${selected}`, { action_type: actionType });
      const eR = await api.get(`/rcm/execution?source_type=denial&source_id=${selected}`);
      setExecTasks(eR.data?.tasks || []);
    } catch(e) { alert(e.response?.data?.detail || 'Failed to create task'); }
    finally { setExecCreating(false); }
  };

  const execAction = async (taskId, action) => {
    try {
      await api.post(`/rcm/execution/${taskId}/${action}`);
      const eR = await api.get(`/rcm/execution?source_type=denial&source_id=${selected}`);
      setExecTasks(eR.data?.tasks || []);
    } catch(e) { alert(e.response?.data?.detail || `${action} failed`); }
  };


  const createCorrected = async () => {
    if (!selected) return;
    try {
      await api.post(`/rcm/denials/${selected}/create-corrected-claim`, { correction_notes: rationale });
      setRationale('');
      load(); openDetail(selected);
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const totalAtRisk = queue.reduce((s, d) => s + (d.denial_amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="denials-tab">
      {/* KPI strip — emphasizes dollars at risk */}
      <div className="flex gap-3">
        <Card className="flex-1 border-red-200"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{fmt(totalAtRisk)}</p><p className="text-[10px] text-muted-foreground uppercase">At Risk</p>
        </CardContent></Card>
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold">{queue.length}</p><p className="text-[10px] text-muted-foreground uppercase">Open Denials</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs">Denial Queue</CardTitle>
          <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {queue.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No denials in queue</p> : (
          <table className="w-full text-xs" data-testid="denial-table">
            <thead><tr className="border-b text-left">
              <th className="py-1.5 font-medium text-muted-foreground text-right">Amount</th>
              <th className="py-1.5 font-medium text-muted-foreground">Code</th>
              <th className="py-1.5 font-medium text-muted-foreground">Description</th>
              <th className="py-1.5 font-medium text-muted-foreground">Payer</th>
              <th className="py-1.5 font-medium text-muted-foreground">Status</th>
              <th className="py-1.5 font-medium text-muted-foreground">Work State</th>
              <th className="py-1.5 font-medium text-muted-foreground">Actions</th>
              <th></th>
            </tr></thead>
            <tbody>{queue.map((d) => (
              <tr key={d.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(d.id)}>
                <td className="py-1.5 text-right font-medium text-red-600">{fmt(d.denial_amount)}</td>
                <td className="py-1.5 font-mono">{d.denial_reason_code}</td>
                <td className="py-1.5 truncate max-w-[180px]">{d.denial_reason_text}</td>
                <td className="py-1.5">{d._payer_name || '—'}</td>
                <td className="py-1.5"><StatusChip status={d.resolution_status} size="xs" /></td>
                <td className="py-1.5">{d.denial_state && <DenialStateBadge state={d.denial_state} />}</td>
                <td className="py-1.5"><span className="text-muted-foreground">{d.available_actions?.length || 0} available</span></td>
                <td className="py-1.5"><ChevronRight className="h-3 w-3 text-muted-foreground" /></td>
              </tr>
            ))}</tbody>
          </table>)}
        </CardContent>
      </Card>

      <SlidePanel open={!!selected} onClose={() => { setSelected(null); setDetail(null); }} title="Denial Detail">
        {detail && (<>
          {/* Denial facts — immutable source data */}
          <Card className="border-red-200 bg-red-50/30">
            <CardContent className="p-3 space-y-1.5 text-xs">
              <p className="font-semibold text-red-700">Denial Facts (source — immutable)</p>
              <div className="flex justify-between"><span>Code</span><span className="font-mono font-bold">{detail.denial_reason_code}</span></div>
              <div className="flex justify-between"><span>Description</span><span className="text-right max-w-[250px]">{detail.denial_reason_text}</span></div>
              <div className="flex justify-between"><span>Category</span><span className="capitalize">{detail.denial_category}</span></div>
              <div className="flex justify-between"><span>Scope</span><span>{detail.denial_scope}</span></div>
              <div className="flex justify-between font-medium text-red-700"><span>Amount at Risk</span><span>{fmt(detail.denial_amount)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Denied</span><span>{detail.denied_at?.slice(0,10)}</span></div>
            </CardContent>
          </Card>

          {/* Suggested Action (Predictive — between facts and decision) */}
          {suggestion && suggestion.suggested_action && (
            <Card className="border-violet-200 bg-violet-50/20" data-testid="denial-suggestion">
              <CardContent className="p-3 space-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <PredictiveLabel />
                  <div className="flex items-center gap-2">
                    <ConfidenceText level={suggestion.confidence} score={suggestion.confidence_score} />
                    <FeedbackButtons predictionId={suggestion.denial_id} />
                  </div>
                </div>
                <p className="font-medium">{suggestion.action_label}</p>
                <p className="text-muted-foreground">{suggestion.reasoning}</p>
                <p className="text-amber-700">Risk: {suggestion.risk_notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Decision panel — status + available actions */}
          {/* Playbook (Phase 5.5 — between suggestion and decision) */}
          {detail._playbook && (
            <Card className="border-sky-200 bg-sky-50/10" data-testid="denial-playbook">
              <CardContent className="p-3 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sky-800">Recommended Workflow</span>
                  <span className="text-[9px] text-muted-foreground">
                    {detail._playbook_match === 'category_fallback' ? 'Category guidance' : 'Exact match'} | v{detail._playbook.version}
                  </span>
                </div>
                {detail._playbook._fallback_note && <p className="text-amber-600 text-[10px]">{detail._playbook._fallback_note}</p>}
                <p className="text-muted-foreground">{detail._playbook.summary}</p>
                {/* Immediate checks */}
                <div><p className="font-medium text-sky-700">Immediate Checks</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-0.5 ml-1">
                    {detail._playbook.immediate_checks?.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
                {/* Step by step */}
                <div><p className="font-medium text-sky-700">Workflow Steps</p>
                  <div className="space-y-1 ml-1">
                    {detail._playbook.step_by_step_workflow?.map((s, i) => (
                      <div key={i} className="flex gap-2"><span className="text-sky-600 font-bold shrink-0">{s.step}.</span><div><span className="font-medium">{s.title}</span> — <span className="text-muted-foreground">{s.instruction}</span></div></div>
                    ))}
                  </div>
                </div>
                {/* Decision paths */}
                <div><p className="font-medium text-sky-700">Decision Paths</p>
                  <div className="space-y-1 ml-1">
                    {detail._playbook.decision_paths?.map((dp, i) => (
                      <div key={i} className="flex gap-2 items-start"><ArrowRight className="h-3 w-3 text-sky-500 mt-0.5 shrink-0" /><span><span className="font-medium capitalize">{dp.path.replace(/_/g, ' ')}</span> → {dp.instruction}</span></div>
                    ))}
                  </div>
                </div>
                {/* Common pitfalls */}
                {detail._playbook.common_pitfalls?.length > 0 && (
                  <div><p className="font-medium text-amber-700">Common Pitfalls</p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5 ml-1">
                      {detail._playbook.common_pitfalls.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ═══ PHASE 6A.4: Denial Workbench Work Area ═══ */}
          {workbench && (
            <Card className="border-blue-200 bg-blue-50/10" data-testid="denial-workbench">
              <CardHeader className="pb-1 pt-3 px-3">
                <CardTitle className="text-xs flex items-center gap-2">
                  Work Area
                  <DenialStateBadge state={wbState} />
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3 space-y-2 text-xs">
                {/* Status fields */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[9px] text-muted-foreground">Denial State</label>
                    <select className="w-full border rounded px-2 py-1 text-xs" value={wbState}
                      onChange={e => setWbState(e.target.value)} data-testid="wb-state-select">
                      {(workbench.allowed_transitions || []).map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                      <option value={workbench.denial?.denial_state}>{(workbench.denial?.denial_state || '').replace(/_/g,' ')} (current)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[9px] text-muted-foreground">Next Step</label>
                    <select className="w-full border rounded px-2 py-1 text-xs" value={wbNextStep}
                      onChange={e => setWbNextStep(e.target.value)} data-testid="wb-nextstep-select">
                      <option value="">Select next step...</option>
                      {(workbench.next_step_options || []).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[9px] text-muted-foreground">Due Date</label>
                  <input type="date" className="w-full border rounded px-2 py-1 text-xs" value={wbDueDate}
                    onChange={e => setWbDueDate(e.target.value)} data-testid="wb-duedate-input" />
                </div>

                {/* Work Notes */}
                <div>
                  <label className="text-[9px] text-muted-foreground">Work Notes / Thought Process</label>
                  <textarea className="w-full border rounded p-2 text-xs min-h-[50px]" rows={2}
                    placeholder="Document your work, reasoning, and next steps..."
                    value={wbNote} onChange={e => setWbNote(e.target.value)} data-testid="wb-note-input" />
                </div>

                {/* Previous notes (appended, timestamped) */}
                {workbench.work_notes?.length > 0 && (
                  <div className="space-y-1 border-t pt-1.5">
                    <p className="text-[9px] text-muted-foreground uppercase">Previous Notes</p>
                    {workbench.work_notes.map((n) => (
                      <div key={n.id} className="bg-white border rounded p-1.5 text-[11px]">
                        <p>{n.text}</p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">{n.created_at?.slice(0,16).replace('T',' ')}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Save Progress */}
                <Button size="sm" className="h-7 text-xs w-full" onClick={saveWorkbenchProgress} disabled={wbSaving} data-testid="wb-save-btn">
                  {wbSaving ? <><RotateCw className="h-3 w-3 mr-1 animate-spin" />Saving...</> : <><FileText className="h-3 w-3 mr-1" />Save Progress</>}
                </Button>

                {/* ═══ ACTION BUTTONS (6A.4-B) ═══ */}
                <div className="flex flex-wrap gap-1.5 pt-1 border-t" data-testid="wb-action-buttons">
                  {workbench.allowed_transitions?.includes('corrected_ready') && (
                    <Button size="sm" className="h-6 text-[10px]" variant="outline"
                      onClick={async () => { setWbSaving(true); try { const r = await api.post(`/rcm/denial-workbench/${selected}/mark-corrected`, { work_note: wbNote }); setWorkbench(r.data); setWbState(r.data.denial?.denial_state || wbState); setWbNote(''); } catch(e) { alert(e.response?.data?.detail || 'Error'); } finally { setWbSaving(false); } }}
                      disabled={wbSaving} data-testid="wb-mark-corrected">
                      <CheckCircle2 className="h-2.5 w-2.5 mr-1" />Corrected — Ready to Bill
                    </Button>
                  )}
                  {workbench.allowed_transitions?.includes('appeal_ready') && (
                    <Button size="sm" className="h-6 text-[10px]" variant="outline"
                      onClick={async () => { setWbSaving(true); try { const r = await api.post(`/rcm/denial-workbench/${selected}/send-appeal`, { work_note: wbNote }); setWorkbench(r.data); setWbState(r.data.denial?.denial_state || wbState); setWbNote(''); } catch(e) { alert(e.response?.data?.detail || 'Error'); } finally { setWbSaving(false); } }}
                      disabled={wbSaving} data-testid="wb-send-appeal">
                      <ShieldAlert className="h-2.5 w-2.5 mr-1" />Send for Appeal
                    </Button>
                  )}
                  {workbench.allowed_transitions?.includes('written_off') && (
                    <Button size="sm" className="h-6 text-[10px]" variant="destructive"
                      onClick={async () => {
                        const reason = prompt('Write-off reason (required):');
                        if (!reason) return;
                        setWbSaving(true);
                        try { const r = await api.post(`/rcm/denial-workbench/${selected}/write-off`, { reason, work_note: wbNote }); setWorkbench(r.data); setWbState(r.data.denial?.denial_state || wbState); setWbNote(''); }
                        catch(e) { alert(e.response?.data?.detail || 'Error'); }
                        finally { setWbSaving(false); }
                      }}
                      disabled={wbSaving} data-testid="wb-write-off">
                      <XCircle className="h-2.5 w-2.5 mr-1" />Write Off
                    </Button>
                  )}
                  {workbench.allowed_transitions?.includes('awaiting_provider') && (
                    <Button size="sm" className="h-6 text-[10px]" variant="outline"
                      onClick={async () => {
                        const msg = prompt('Message to provider (required):');
                        if (!msg) return;
                        setWbSaving(true);
                        try { const r = await api.post(`/rcm/denial-workbench/${selected}/send-to-provider`, { message: msg, work_note: wbNote }); setWorkbench(r.data); setWbState(r.data.denial?.denial_state || wbState); setWbNote(''); }
                        catch(e) { alert(e.response?.data?.detail || 'Error'); }
                        finally { setWbSaving(false); }
                      }}
                      disabled={wbSaving} data-testid="wb-send-provider">
                      <Send className="h-2.5 w-2.5 mr-1" />Send to Provider
                    </Button>
                  )}
                </div>

                {/* Active follow-up */}
                {workbench.active_followup && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-1.5 text-[10px]" data-testid="wb-followup">
                    <span className="font-medium text-amber-700">Follow-up:</span> {workbench.active_followup.next_step} — due {workbench.active_followup.due_date}
                  </div>
                )}

                {/* Workbench action history */}
                {workbench.actions?.length > 0 && (
                  <div className="space-y-0.5 border-t pt-1.5">
                    <p className="text-[9px] text-muted-foreground uppercase">Action History</p>
                    {workbench.actions.slice(0, 8).map((a) => (
                      <div key={a.id} className="text-[10px] text-muted-foreground">
                        {a.created_at?.slice(11,16)} — {a.summary}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ═══ PHASE 6A.5: Execution Panel ═══ */}
          {workbench && (
            <Card className="border-indigo-200 bg-indigo-50/10" data-testid="execution-panel">
              <CardHeader className="pb-1 pt-3 px-3">
                <CardTitle className="text-xs flex items-center gap-2">
                  Execution Bridge
                  {execTasks.filter(t => t.status === 'pending').length > 0 && (
                    <Badge variant="outline" className="text-[9px] text-indigo-600 border-indigo-300">{execTasks.filter(t => t.status === 'pending').length} pending</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3 space-y-2 text-xs">
                {/* Create buttons — only shown when denial_state is action-ready */}
                {wbState === 'corrected_ready' && !execTasks.some(t => t.action_type === 'rebill' && ['pending','in_progress'].includes(t.status)) && (
                  <Button size="sm" className="h-7 text-xs w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    onClick={() => createExecTask('rebill')} disabled={execCreating} data-testid="exec-create-rebill">
                    {execCreating ? 'Creating...' : 'Create Rebill Task'}
                  </Button>
                )}
                {wbState === 'appeal_ready' && !execTasks.some(t => t.action_type === 'appeal' && ['pending','in_progress'].includes(t.status)) && (
                  <Button size="sm" className="h-7 text-xs w-full bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={() => createExecTask('appeal')} disabled={execCreating} data-testid="exec-create-appeal">
                    {execCreating ? 'Creating...' : 'Create Appeal Task'}
                  </Button>
                )}
                {wbState === 'written_off' && !execTasks.some(t => t.action_type === 'writeoff' && ['pending','in_progress'].includes(t.status)) && (
                  <Button size="sm" className="h-7 text-xs w-full bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => createExecTask('writeoff')} disabled={execCreating} data-testid="exec-create-writeoff">
                    {execCreating ? 'Creating...' : 'Create Write-Off Task'}
                  </Button>
                )}

                {/* Existing execution tasks */}
                {execTasks.length > 0 && (
                  <div className="space-y-1.5 pt-1 border-t">
                    {execTasks.map((t) => {
                      const statusColors = {
                        pending: 'bg-amber-100 text-amber-700 border-amber-200',
                        in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
                        completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                        failed: 'bg-red-100 text-red-700 border-red-200',
                        cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
                      };
                      return (
                        <div key={t.id} className="border rounded p-2 space-y-1" data-testid={`exec-task-${t.id?.slice(0,8)}`}>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium capitalize">{t.action_type}</span>
                              <span className={`text-[9px] font-medium border rounded px-1.5 py-0.5 ${statusColors[t.status] || ''}`}>{t.status}</span>
                            </div>
                            <span className="text-[10px] text-muted-foreground">{t.created_at?.slice(0,16).replace('T',' ')}</span>
                          </div>
                          {t.result_summary && <p className="text-[10px] text-muted-foreground">{t.result_summary}</p>}
                          {t.payload_snapshot_id && <p className="text-[9px] text-muted-foreground font-mono">Snapshot: {t.payload_snapshot_id?.slice(0,12)}...</p>}
                          {/* Action buttons — Create and Execute are SEPARATE actions */}
                          <div className="flex gap-1">
                            {t.status === 'pending' && (
                              <>
                                <Button size="sm" className="h-5 text-[10px] px-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                                  onClick={() => execAction(t.id, 'execute')} data-testid={`exec-execute-${t.id?.slice(0,8)}`}>
                                  Execute
                                </Button>
                                <Button size="sm" variant="ghost" className="h-5 text-[10px] px-2"
                                  onClick={() => execAction(t.id, 'cancel')} data-testid={`exec-cancel-${t.id?.slice(0,8)}`}>
                                  Cancel
                                </Button>
                              </>
                            )}
                            {t.status === 'failed' && (
                              <Button size="sm" variant="outline" className="h-5 text-[10px] px-2"
                                onClick={() => execAction(t.id, 'retry')} data-testid={`exec-retry-${t.id?.slice(0,8)}`}>
                                Retry (same payload)
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {execTasks.length === 0 && !['corrected_ready','appeal_ready','written_off'].includes(wbState) && (
                  <p className="text-[10px] text-muted-foreground text-center py-1">No execution tasks. Move denial to an action-ready state first.</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Decision panel (original) — status + available actions */}
          <Card className="border-primary/20">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">Current status:</span>
                <StatusChip status={detail.resolution_status} />
              </div>

              {/* Rationale input — always visible when actions available */}
              {detail.available_actions?.length > 0 && (
                <div className="space-y-2">
                  <textarea className="w-full text-xs border rounded p-2" rows={2}
                    placeholder="Action rationale (required for write-off)..."
                    value={rationale} onChange={e => setRationale(e.target.value)} data-testid="denial-rationale" />
                  {detail.resolution_status === 'under_review' && (
                    <textarea className="w-full text-xs border rounded p-2" rows={1}
                      placeholder="Provider note (optional)..."
                      value={provNote} onChange={e => setProvNote(e.target.value)} data-testid="provider-note" />
                  )}
                </div>
              )}

              {/* Action buttons — mapped from available_actions */}
              <div className="flex flex-wrap gap-2">
                {detail.available_actions?.map((a) => {
                  const icons = {
                    review: Eye, send_to_provider: CornerDownRight, mark_corrected: CheckCircle2,
                    mark_appeal: ShieldAlert, file_appeal: Send, write_off: XCircle,
                    rebill: ArrowRight, mark_resolved: CheckCircle2, close: X,
                  };
                  const Icon = icons[a.action] || ArrowRight;
                  const variant = a.action === 'write_off' ? 'destructive' : 'outline';
                  return (
                    <Button key={a.action} size="sm" className="h-7 text-xs" variant={variant}
                      onClick={() => a.action === 'write_off' && !rationale ? alert('Write-off requires rationale') : doAction(a.action)}
                      data-testid={`action-${a.action}`}>
                      <Icon className="h-3 w-3 mr-1" />{a.label}
                    </Button>
                  );
                })}
                {detail.resolution_status === 'corrected_claim_ready' && (
                  <Button size="sm" className="h-7 text-xs" onClick={createCorrected} data-testid="action-create-corrected">
                    <ListChecks className="h-3 w-3 mr-1" />Create Corrected Claim
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Corrected claims lineage */}
          {detail.corrected_claims?.length > 0 && (
            <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Corrected Claims</CardTitle></CardHeader>
              <CardContent className="px-3 pb-3 space-y-1.5">
                {detail.corrected_claims.map((cc, i) => (
                  <div key={i} className="text-xs border-b pb-1.5 last:border-0">
                    <div className="flex justify-between"><span className="font-medium">Rebill #{cc.resubmission_number}</span><StatusChip status={cc.status} size="xs" /></div>
                    <p className="text-muted-foreground">{cc.rebill_reason}</p>
                    {cc.correction_notes && <p className="text-sky-700">Notes: {cc.correction_notes}</p>}
                  </div>
                ))}
            </CardContent></Card>
          )}

          {/* Workflow history */}
          <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Action History</CardTitle></CardHeader>
            <CardContent className="px-3 pb-3">
              <Timeline events={detail.workflow_history} />
          </CardContent></Card>
        </>)}
      </SlidePanel>
    </div>
  );
}

// ═══════════════════════════════════════
// REMITTANCE TAB (Group 2)
// ═══════════════════════════════════════

export { DenialsTab };
